import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Search, Loader2, Eye, Clock, TriangleAlert } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { apiService } from "@/api/api";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { format } from 'date-fns';

interface Note {
  session_id: string;
  session_title?: string;
  session_type?: string;
  session_timestamp: string;
  session_status: string;
  client_id?: string;
  clientFullName?: string;
  clientLoading?: boolean;
  [key: string]: any;
}

// Cache for formatted dates to avoid recalculation
const dateFormatCache = new Map<string, string>();

const NotesPanel = () => {
  const [notes, setNotes] = useState<Note[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [displayCount, setDisplayCount] = useState(10); // Initially show 10 items
  const navigate = useNavigate();
  const { toast } = useToast();

  // Use useRef for client cache so it persists during component lifecycle but resets on mount
  const clientCacheRef = useRef(new Map<string, { first_name: string; last_name: string }>());
  const clientCache = clientCacheRef.current;

  // Fetch client data for a note with debouncing
  const fetchClientData = useCallback(async (note: Note) => {
    if (!note.client_id || clientCache.has(note.client_id)) {
      // If already cached, update immediately
      if (clientCache.has(note.client_id)) {
        const cached = clientCache.get(note.client_id)!;
        const clientFullName = `${cached.first_name || ''} ${cached.last_name || ''}`.trim() || 'Unknown Patient';
        setNotes(prev => prev.map(n =>
          n.client_id === note.client_id
            ? { ...n, clientFullName, clientLoading: false }
            : n
        ));
      }
      return;
    }

    try {
      // Mark this note's client as loading
      setNotes(prev => prev.map(n =>
        n.session_id === note.session_id
          ? { ...n, clientLoading: true }
          : n
      ));

      const clientResponse = await apiService.getClient(note.client_id);
      if (clientResponse?.success && clientResponse.data) {
        const { first_name, last_name } = clientResponse.data;
        const clientInfo = { first_name, last_name };
        const clientFullName = `${first_name || ''} ${last_name || ''}`.trim() || 'Unknown Patient';

        // Cache the client data
        clientCache.set(note.client_id, clientInfo);

        // Update all notes with this client_id
        setNotes(prev => prev.map(n =>
          n.client_id === note.client_id
            ? {
                ...n,
                clientFullName,
                clientLoading: false
              }
            : n
        ));
      }
    } catch (error) {
      console.error(`Error fetching client name for ID ${note.client_id}:`, error);
      // Mark as failed but don't block the UI
      setNotes(prev => prev.map(n =>
        n.session_id === note.session_id
          ? { ...n, clientLoading: false, clientFullName: 'Unknown Patient' }
          : n
      ));
    }
  }, []);

  // Batch fetch client data with improved concurrency control
  const fetchClientDataBatch = useCallback(async (notes: Note[]) => {
    const BATCH_SIZE = 10; // Increased batch size for better performance
    const notesNeedingClientData = notes.filter(n =>
      n.client_id && !clientCache.has(n.client_id) && !n.clientFullName
    );

    // Early return if no data needed
    if (notesNeedingClientData.length === 0) return;

    console.log(`Fetching client data for ${notesNeedingClientData.length} sessions`);

    // Process all batches in parallel with limited concurrency
    const batches = [];
    for (let i = 0; i < notesNeedingClientData.length; i += BATCH_SIZE) {
      const batch = notesNeedingClientData.slice(i, i + BATCH_SIZE);
      batches.push(batch);
    }

    // Process batches with controlled concurrency
    for (const batch of batches) {
      await Promise.allSettled(batch.map(fetchClientData));

      // Reduced delay for better performance
      if (batches.indexOf(batch) < batches.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }
  }, [fetchClientData]);

  // Robust date parsing helper
  const parseSessionTimestamp = (timestamp: string): Date => {
    if (!timestamp) return new Date(0); // Return epoch for invalid dates

    try {
      let cleanedTimestamp = timestamp;

      // Handle microseconds - truncate to milliseconds if present
      if (cleanedTimestamp.includes('.')) {
        const dotIndex = cleanedTimestamp.indexOf('.');
        const beforeDot = cleanedTimestamp.substring(0, dotIndex);
        const afterDot = cleanedTimestamp.substring(dotIndex + 1);

        // Check if there's timezone info after microseconds
        const timezoneMatch = afterDot.match(/([+-]\d{2}:?\d{2}|Z)$/);
        if (timezoneMatch) {
          // Has timezone - truncate microseconds to milliseconds
          const microseconds = afterDot.replace(timezoneMatch[0], '');
          const milliseconds = microseconds.substring(0, 3).padEnd(3, '0');
          cleanedTimestamp = `${beforeDot}.${milliseconds}${timezoneMatch[0]}`;
        } else {
          // No timezone info - assume UTC and truncate microseconds
          const milliseconds = afterDot.substring(0, 3).padEnd(3, '0');
          cleanedTimestamp = `${beforeDot}.${milliseconds}Z`;
        }
      } else if (!cleanedTimestamp.match(/([+-]\d{2}:?\d{2}|Z)$/)) {
        // No microseconds and no timezone - assume UTC
        cleanedTimestamp = `${cleanedTimestamp}Z`;
      }

      const date = new Date(cleanedTimestamp);

      // Validate the date
      if (isNaN(date.getTime())) {
        console.error('Invalid date parsed:', cleanedTimestamp, 'from original:', timestamp);
        return new Date(0);
      }

      return date;
    } catch (error) {
      console.error('Date parsing error for timestamp:', timestamp, error);
      return new Date(0);
    }
  };

  // Helper function to sort notes by timestamp (newest first)
  const sortNotesByTimestamp = (notes: Note[]) => {
    return [...notes].sort((a, b) => {
      const dateA = parseSessionTimestamp(a.session_timestamp);
      const dateB = parseSessionTimestamp(b.session_timestamp);

      // Sort in descending order (newest first)
      // If dates are equal or invalid, sort by session_id as fallback
      const timeDiff = dateB.getTime() - dateA.getTime();
      if (timeDiff === 0) {
        return (b.session_id || '').localeCompare(a.session_id || '');
      }
      return timeDiff;
    });
  };

  const fetchNotes = useCallback(async (isBackground = false) => {
    try {
      if (!isBackground) {
        setLoading(true); // only show spinner when NOT background polling
      }

      // Add timestamp to track fetch duration
      const fetchStartTime = performance.now();

      const apiResponse = await apiService.getSessionsByCaregiver();

      let allSessions: Note[] = [];
      if (apiResponse && typeof apiResponse === 'object' && 'data' in apiResponse) {
        allSessions = Array.isArray(apiResponse.data) ? apiResponse.data : [];
      } else if (Array.isArray(apiResponse)) {
        allSessions = apiResponse;
      }

      // Filter sessions that are not completed and have valid timestamps
      const filteredSessions = allSessions.filter(
        (session: Note) => {
          // Exclude completed sessions
          if (session.session_status === "completed") return false;

          // Validate timestamp exists
          if (!session.session_timestamp) {
            console.warn('Session missing timestamp:', session.session_id);
            return false;
          }

          return true;
        }
      );

      // Sort sessions by timestamp (newest first) with improved parsing
      const sortedSessions = sortNotesByTimestamp(filteredSessions);

      // Log performance metrics
      const fetchDuration = performance.now() - fetchStartTime;
      console.log(`Fetch completed in ${fetchDuration.toFixed(2)}ms, found ${sortedSessions.length} sessions`);

      // Preserve existing client names when updating during background polling
      if (isBackground) {
        setNotes(prevNotes => {
          // Create a map of existing client data for quick lookup
          const existingClientData = new Map(
            prevNotes.map(note => [
              note.session_id, 
              { 
                clientFullName: note.clientFullName, 
                clientLoading: note.clientLoading 
              }
            ])
          );

          // Merge new session data with existing client data
          const updatedNotes = sortedSessions.map(session => {
            const existingData = existingClientData.get(session.session_id);
            return {
              ...session,
              clientFullName: existingData?.clientFullName,
              clientLoading: existingData?.clientLoading || false
            };
          });

          return updatedNotes;
        });
      } else {
        // Initial load - set notes without client data
        setNotes(sortedSessions);
      }
      
      if (!isBackground) {
        setLoading(false);
      }

      // Fetch client data for new sessions or sessions without client data
      // Use requestIdleCallback for better performance
      if (sortedSessions.length > 0) {
        const sessionsNeedingClientData = sortedSessions.filter(session => {
          return session.client_id && !clientCache.has(session.client_id);
        });

        if (sessionsNeedingClientData.length > 0) {
          // Use requestIdleCallback if available, otherwise setTimeout
          if ('requestIdleCallback' in window) {
            requestIdleCallback(() => {
              fetchClientDataBatch(sessionsNeedingClientData);
            }, { timeout: 2000 });
          } else {
            setTimeout(() => {
              fetchClientDataBatch(sessionsNeedingClientData);
            }, 100);
          }
        }
      }

    } catch (error) {
      console.error("Error fetching notes:", error);
      if (!isBackground) {
        setNotes([]);
        toast({
          title: "Error fetching notes",
          description: "Failed to load notes for review. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      if (!isBackground) {
        setLoading(false);
      }
    }
  }, [toast, fetchClientDataBatch]);

  useEffect(() => {
    // Clear the cache ref on mount to ensure fresh data when navigating back
    clientCacheRef.current.clear();

    // first load → show spinner
    fetchNotes(false);

    // background polling → no spinner (reduced interval for better UX)
    const interval = setInterval(() => {
      fetchNotes(true);
    }, 60000); // Reduced from 90s to 60s for better responsiveness

    // Also refetch when tab becomes visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchNotes(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchNotes]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Memoized filtering and pagination for performance
  const { filteredNotes, displayedNotes, hasMore } = useMemo(() => {
    let filtered = notes;

    // Apply search filter if search term exists
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      filtered = notes.filter(note => {
        const titleMatch = (note.session_title || note.title || '').toLowerCase().includes(searchLower);
        const clientNameMatch = (note.clientFullName || '').toLowerCase().includes(searchLower);
        const typeMatch = (note.session_type || '').toLowerCase().includes(searchLower);
        const statusMatch = (note.session_status || '').toLowerCase().includes(searchLower);

        return titleMatch || clientNameMatch || typeMatch || statusMatch;
      });
    }

    // Apply display limit
    const displayed = filtered.slice(0, displayCount);
    const hasMoreItems = filtered.length > displayCount;

    return {
      filteredNotes: filtered,
      displayedNotes: displayed,
      hasMore: hasMoreItems
    };
  }, [notes, searchTerm, displayCount]);

  // Reset display count when search changes
  useEffect(() => {
    setDisplayCount(10);
  }, [searchTerm]);

  // Load more handler
  const handleLoadMore = useCallback(() => {
    setDisplayCount(prev => Math.min(prev + 10, filteredNotes.length));
  }, [filteredNotes.length]);

  const handleReviewClick = useCallback((session_id: string, session_status: string, clientName: string, sessionDate: string, sessionTitle: string) => {
    console.log('🔥 BUTTON CLICKED! handleReviewClick called');
    console.log('Parameters:', { session_id, session_status, clientName, sessionDate, sessionTitle });
  
  if (session_status === 'ready_for_review') {
    navigate(`/refine-note/${session_id}`, {
      state: {
        clientName,
        sessionDate,
        sessionTitle
      }
    });
  } else if (session_status === 'processing') {
    toast({
      title: "Note is still processing",
      description: `The note for "${sessionTitle}" is being generated. Please wait a moment.`,
      variant: "default",
    });
  } else {
    toast({
      title: "Note Generation Error", 
      description: `The note for "${sessionTitle}" encountered an error during generation. Please contact support.`,
      variant: "destructive",
    });
  }
}, [navigate, toast]); // Add dependencies
  // Updated function to properly convert to local browser time
 const formatLocalDateTime = (dateString: string) => {
  if (!dateString) return "Unknown date";

  // Check cache first
  if (dateFormatCache.has(dateString)) {
    return dateFormatCache.get(dateString)!;
  }

  try {
    // Clean up the timestamp to ensure proper parsing
    let cleanedDateString = dateString;
    
    // Handle microseconds - truncate to milliseconds if present
    if (cleanedDateString.includes('.')) {
      const dotIndex = cleanedDateString.indexOf('.');
      const beforeDot = cleanedDateString.substring(0, dotIndex);
      const afterDot = cleanedDateString.substring(dotIndex + 1);
      
      // Check if there's timezone info after microseconds
      const timezoneMatch = afterDot.match(/([+-]\d{2}:?\d{2}|Z)$/);
      if (timezoneMatch) {
        // Has timezone - truncate microseconds to milliseconds
        const microseconds = afterDot.replace(timezoneMatch[0], '');
        const milliseconds = microseconds.substring(0, 3);
        cleanedDateString = `${beforeDot}.${milliseconds}${timezoneMatch[0]}`;
      } else {
        // No timezone info - assume UTC and truncate microseconds
        const milliseconds = afterDot.substring(0, 3);
        cleanedDateString = `${beforeDot}.${milliseconds}Z`;
      }
    } else if (!cleanedDateString.match(/([+-]\d{2}:?\d{2}|Z)$/)) {
      // No microseconds and no timezone - assume UTC
      cleanedDateString = `${cleanedDateString}Z`;
    }
    
    // Create date object - this should now parse correctly
    const date = new Date(cleanedDateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.error('Invalid date parsed:', cleanedDateString);
      return "Invalid date";
    }
    
    // Debug: Log the original vs parsed time
    console.log('Original timestamp:', dateString);
    console.log('Cleaned timestamp:', cleanedDateString);
    console.log('Parsed UTC date:', date.toISOString());
    console.log('Local time:', date.toString());
    
    // Format in local timezone
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    };
    
    const formatted = new Intl.DateTimeFormat('en-US', options).format(date);
    console.log('Formatted local time:', formatted);

    // Cache the result
    dateFormatCache.set(dateString, formatted);

    return formatted;
  } catch (error) {
    console.error('Date formatting error:', error);
    return "Invalid date";
  }
};

  const getSessionTypeBadgeColor = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'consultation':
      case 'online': 
        return 'bg-blue-100 text-blue-800';
      case 'dictation':
        return 'bg-green-100 text-green-800';
      case 'therapy':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Component for displaying client name with loading state
  const ClientNameDisplay = ({ note }: { note: Note }) => {
    if (note.clientLoading) {
      return (
        <div className="flex items-center gap-1">
          <div className="animate-pulse bg-gray-200 h-4 w-24 rounded"></div>
        </div>
      );
    }
    return <span>{note.clientFullName || "Loading..."}</span>;
  };

  // Skeleton loader component
  const NoteSkeleton = () => (
    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 animate-pulse">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <div className="h-5 bg-gray-200 rounded w-48"></div>
            <div className="h-5 bg-gray-200 rounded-full w-20"></div>
          </div>
          <div className="h-4 bg-gray-200 rounded w-36"></div>
          <div className="h-4 bg-gray-200 rounded w-32"></div>
        </div>
        <div className="h-10 bg-gray-200 rounded-full w-24"></div>
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-lg shadow-sm p-5 mb-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-5 gap-3">
        <div className="flex items-center">
          <h2 className="text-xl font-semibold">Notes to review</h2>
          <span className="ml-2 bg-psybytes-purple text-white text-xs rounded-full px-2 h-6 flex items-center justify-center min-w-[24px]">
            {loading ? "..." : filteredNotes.length}
          </span>
        </div>
        <div className="relative w-full sm:w-64">
          <Input
            type="text"
            placeholder="Search notes..."
            className="pl-9 pr-3 py-2 rounded-lg border border-gray-300 focus:ring-1 focus:ring-black focus:border-black w-full transition-colors"
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        </div>
      </div>
      
      {loading ? (
        <div className="space-y-4">
          {/* Show skeleton loaders while loading */}
          {[1, 2, 3].map(i => (
            <NoteSkeleton key={i} />
          ))}
        </div>
      ) : displayedNotes.length === 0 ? (
        <div className="text-center py-10 animate-fade-in">
          <div className="text-lg font-medium text-gray-700 mb-2">Hurray 🎉!</div>
          <p className="text-gray-500">
            {searchTerm ? "No notes match your search." : "You have reviewed all the notes."}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {displayedNotes.map(note => {
            const isProcessing = note.session_status === 'processing';
            const isErrorOrFailed = 
              note.session_status === 'error' || note.session_status === 'failed';
            const isReadyForReview = note.session_status === 'ready_for_review';


            return (
                <div 
                  key={note.session_id} 
                  className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-gray-300 transition-colors"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">
                          {note.session_title || 'Untitled Session'}
                        </h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSessionTypeBadgeColor(note.session_type)}`}>
                          {note.session_type || 'Session'}
                        </span>

                        {isProcessing && (
                          <span className="ml-2 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 flex items-center gap-1">
                            <Clock className="h-3 w-3 animate-spin" /> Processing...
                          </span>
                        )}

                        {(note.session_status === 'error' || note.session_status === 'failed') && (
                          <span className="ml-2 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 flex items-center gap-1">
                            <TriangleAlert className="h-3 w-3" /> {note.session_status === 'failed' ? 'Failed' : 'Error'}
                          </span>
                        )}

                        {isReadyForReview && (
                          <span className="ml-2 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 flex items-center gap-1">
                            Ready for Review
                          </span>
                        )}
                      </div>

                      <div className="text-sm text-gray-600">
                        Session Date: {formatLocalDateTime(note.session_timestamp)}
                      </div>
                      <div className="text-sm text-gray-600 flex items-center gap-2">
                        Patient: <ClientNameDisplay note={note} />
                      </div>
                    </div>

                    {/* ✅ Show button only if NOT error/failed */}
                    {note.session_status !== 'error' && note.session_status !== 'failed' && (
                      <div className="flex items-center">
                        <Button
                          type="button"
                          onClick={() => {
                            console.log("clicked review");
                            handleReviewClick(
                              note.session_id, 
                              note.session_status,
                              note.clientFullName || 'Unknown Patient', 
                              note.session_timestamp,
                              note.session_title
                            );
                          }}
                          className={`flex items-center gap-2 rounded-full ${
                            isProcessing 
                              ? 'bg-gray-400 cursor-not-allowed' 
                              : 'bg-black hover:bg-black/90 text-white'
                          } transition-colors`}
                          disabled={isProcessing}
                        >
                          {isProcessing ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Processing...
                            </>
                          ) : (
                            <>
                              <Eye className="h-4 w-4" />
                              Review
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
          })}

          {/* Load More Button */}
          {hasMore && (
            <div className="mt-6 text-center">
              <Button
                onClick={handleLoadMore}
                variant="outline"
                className="border-gray-300 hover:bg-gray-50"
              >
                Load More ({filteredNotes.length - displayCount} remaining)
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotesPanel;